import React from 'react';
import { motion } from 'framer-motion';
import { DashboardNav } from '../components/dashboard/DashboardNav';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { RotateCcw, Shield, ShieldAlert, Users } from 'lucide-react';
export function UserManagement() {
  const {
    users,
    resetUserPassword,
    user: currentUser
  } = useAuth();
  // Filter users based on admin region
  const regionFilteredUsers = users.filter(user => {
    // Main admin sees all users
    if (currentUser?.adminType === 'main') {
      return true;
    }
    // Regional admins only see users from their region
    if (currentUser?.adminType === 'regional') {
      return user.region === currentUser.region;
    }
    return false;
  });
  return <div className="min-h-screen bg-rose-50/30 flex" data-id="element-3119">
      <DashboardNav data-id="element-3120" />

      <main className="flex-1 p-4 lg:p-8 overflow-y-auto h-screen" data-id="element-3121">
        <div className="max-w-7xl mx-auto space-y-8" data-id="element-3122">
          <header className="mb-8" data-id="element-3123">
            <h1 className="text-2xl font-bold text-gray-900 font-serif mb-2" data-id="element-3124">
              User Management
            </h1>
            <p className="text-gray-500" data-id="element-3125">
              Manage registered users and their account access
              {currentUser?.adminType === 'regional' && <span className="ml-2 text-rose-500 font-medium" data-id="element-3126">
                  ({currentUser.region} Region Only)
                </span>}
            </p>
          </header>

          {/* Stats Card */}
          <div className="bg-white rounded-xl p-6 border border-rose-100 shadow-sm" data-id="element-3127">
            <div className="flex items-center justify-between" data-id="element-3128">
              <div data-id="element-3129">
                <p className="text-sm text-gray-600 mb-1" data-id="element-3130">Total Users</p>
                <p className="text-3xl font-bold text-gray-900" data-id="element-3131">
                  {regionFilteredUsers.length}
                </p>
                {currentUser?.adminType === 'regional' && <p className="text-xs text-gray-500 mt-1" data-id="element-3132">
                    in {currentUser.region}
                  </p>}
              </div>
              <Users className="w-12 h-12 text-rose-500" data-id="element-3133" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-rose-100 overflow-hidden" data-id="element-3134">
            <div className="overflow-x-auto" data-id="element-3135">
              <table className="w-full text-left border-collapse" data-id="element-3136">
                <thead data-id="element-3137">
                  <tr className="bg-rose-50/50 border-b border-rose-100" data-id="element-3138">
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3139">
                      User Details
                    </th>
                    {currentUser?.adminType === 'main' && <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3140">
                        Region
                      </th>}
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3141">
                      Contact Info
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3142">
                      Password Status
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3143">
                      Consents
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-3144">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-50" data-id="element-3145">
                  {regionFilteredUsers.map(user => <motion.tr key={user.id} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="hover:bg-rose-50/30 transition-colors" data-id="element-3146">
                      <td className="px-6 py-4" data-id="element-3147">
                        <div data-id="element-3148">
                          <p className="font-medium text-gray-900" data-id="element-3149">
                            {user.name} {user.surname}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-3150">
                            Joined{' '}
                            {new Date(user.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </td>
                      {currentUser?.adminType === 'main' && <td className="px-6 py-4" data-id="element-3151">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-3152">
                            {user.region || 'N/A'}
                          </span>
                        </td>}
                      <td className="px-6 py-4" data-id="element-3153">
                        <div className="space-y-1" data-id="element-3154">
                          <p className="text-sm text-gray-600" data-id="element-3155">{user.email}</p>
                          <p className="text-sm text-gray-600" data-id="element-3156">
                            {user.cellNumber || 'No phone'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-3157">
                        {user.hasDefaultPassword ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-3158">
                            <ShieldAlert className="w-3 h-3 mr-1" data-id="element-3159" />
                            Default
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-3160">
                            <Shield className="w-3 h-3 mr-1" data-id="element-3161" />
                            Custom
                          </span>}
                      </td>
                      <td className="px-6 py-4" data-id="element-3162">
                        <div className="flex flex-col gap-1 text-xs text-gray-500" data-id="element-3163">
                          <span className={user.emailMarketingConsent ? 'text-green-600' : 'text-gray-400'} data-id="element-3164">
                            • Email Marketing
                          </span>
                          <span className={user.orderUpdatesConsent ? 'text-green-600' : 'text-gray-400'} data-id="element-3165">
                            • Order Updates
                          </span>
                          <span className={user.whatsappConsent ? 'text-green-600' : 'text-gray-400'} data-id="element-3166">
                            • WhatsApp
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-3167">
                        <Button variant="outline" size="sm" onClick={() => resetUserPassword(user.id)} leftIcon={<RotateCcw className="w-3 h-3" data-id="element-3169" />} className="text-xs" data-id="element-3168">
                          Reset Password
                        </Button>
                      </td>
                    </motion.tr>)}
                  {regionFilteredUsers.length === 0 && <tr data-id="element-3170">
                      <td colSpan={currentUser?.adminType === 'main' ? 6 : 5} className="px-6 py-12 text-center text-gray-500" data-id="element-3171">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-3" data-id="element-3172" />
                        <p className="font-medium" data-id="element-3173">No users found</p>
                        <p className="text-sm mt-1" data-id="element-3174">
                          {currentUser?.adminType === 'regional' ? `No users registered in ${currentUser.region} region yet` : 'No users registered yet'}
                        </p>
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>;
}